/**
 * Remembers which sign-in method the user chose last, so the auth prompt can
 * badge it ("آخر استخدام") next time. Convenience only: it is never read for
 * authorization, and a missing value simply means no badge.
 *
 * Social sign-in leaves the page for the 00acc OAuth bounce, so the click
 * handler cannot know whether it succeeded. The click records a *pending*
 * method and the next boot with a live session promotes it, which means a
 * cancelled OAuth leaves the previous badge untouched.
 *
 * Mirrors home/src/lib/lastAuthMethod.ts in the zero repo. Both run on the pay
 * origin, so on web they read and write the same localStorage entry.
 */
export type AuthMethod = 'password' | 'emailCode' | 'google' | 'discord' | 'apple';

const CONFIRMED_KEY = 'zero.lastAuthMethod';
const PENDING_KEY = 'zero.pendingAuthMethod';

const VALID: readonly AuthMethod[] = ['password', 'emailCode', 'google', 'discord', 'apple'];

function read(key: string): AuthMethod | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(key) as AuthMethod | null;
    return v && VALID.includes(v) ? v : null;
  } catch {
    // Safari private mode and storage-blocked webviews throw on access.
    return null;
  }
}

function write(key: string, value: AuthMethod | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch { /* ignore — the badge is optional */ }
}

/** The method to badge, or null when we have never seen a successful sign-in. */
export function getLastAuthMethod(): AuthMethod | null {
  return read(CONFIRMED_KEY);
}

/** Record a method we know succeeded (email + password, which stays on-page). */
export function rememberAuthMethod(method: AuthMethod): void {
  write(CONFIRMED_KEY, method);
  write(PENDING_KEY, null);
}

/** Record a method we are about to redirect into, pending confirmation. */
export function rememberPendingAuthMethod(method: AuthMethod): void {
  write(PENDING_KEY, method);
}

/** Called on a boot with a live session: the pending redirect worked. */
export function confirmPendingAuthMethod(): void {
  const pending = read(PENDING_KEY);
  if (pending) rememberAuthMethod(pending);
}

/* Last email used ───────────────────────────────────────────────────────── */

const EMAIL_KEY = 'zero.lastAuthEmail';

/** The address to prefill, so a returning user only supplies the secret. */
export function getLastAuthEmail(): string {
  if (typeof window === 'undefined') return '';
  try {
    const v = window.localStorage.getItem(EMAIL_KEY) || '';
    // Guard against a stale or hand-edited entry ending up in the field.
    return v.length <= 254 && v.includes('@') ? v : '';
  } catch {
    return '';
  }
}

/** Remember the address a sign-in was attempted with. */
export function rememberAuthEmail(email: string): void {
  if (typeof window === 'undefined') return;
  const trimmed = String(email || '').trim();
  try {
    if (trimmed && trimmed.includes('@')) window.localStorage.setItem(EMAIL_KEY, trimmed);
  } catch { /* ignore — the prefill is optional */ }
}

/** "Not you?" — drop the remembered address and method. */
export function forgetAuthIdentity(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(EMAIL_KEY);
    window.localStorage.removeItem(CONFIRMED_KEY);
    window.localStorage.removeItem(PENDING_KEY);
  } catch { /* ignore */ }
}
